if (!customElements.get('product-upsell')) {
  customElements.define(
    'product-upsell',
    class ProductUpsell extends HTMLElement {
      constructor() {
        super();
        this.onToggleClick = this.onToggleClick.bind(this);
        this.onProductBuyClick = this.onProductBuyClick.bind(this);
        this.onProductFormSubmit = this.onProductFormSubmit.bind(this);
        this.onExternalCartUpdate = this.onExternalCartUpdate.bind(this);
        this.onCartLinesUpdate = this.onCartLinesUpdate.bind(this);
        this.onCartError = this.onCartError.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.cartMutationPromises = new Set();
        this.cartStateSequence = 0;
        this.appliedCartStateSequence = 0;
        this.lastCartItemCount = null;
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.addEventListener('click', this.onToggleClick);
        this.productInfo?.addEventListener('click', this.onProductBuyClick, true);
        this.productInfo?.addEventListener('submit', this.onProductFormSubmit, true);
        document.addEventListener('vioran:cart-updated', this.onExternalCartUpdate);
        document.addEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
        window.addEventListener('pageshow', this.onPageShow);
        window.addEventListener('focus', this.onPageShow);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError);
        }

        this.refreshCartState();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onToggleClick);
        this.productInfo?.removeEventListener('click', this.onProductBuyClick, true);
        this.productInfo?.removeEventListener('submit', this.onProductFormSubmit, true);
        document.removeEventListener('vioran:cart-updated', this.onExternalCartUpdate);
        document.removeEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
        window.removeEventListener('pageshow', this.onPageShow);
        window.removeEventListener('focus', this.onPageShow);
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.cartErrorUnsubscriber) this.cartErrorUnsubscriber();
        if (this.deferredAddTimer) window.clearTimeout(this.deferredAddTimer);
      }

      onToggleClick(event) {
        const button = event.target.closest('.product-upsell-toggle');
        if (!button || !this.contains(button) || button.disabled) return;

        event.preventDefault();
        const state = button.dataset.state || 'idle';
        if (state === 'idle') {
          this.handleIdleToggle(button);
        } else if (state === 'pending') {
          this.setButtonState(button, 'idle');
        } else if (state === 'cart') {
          this.removeCartItem(button);
        }
      }

      setButtonState(button, state, lineKey = '') {
        const isSelected = state !== 'idle';
        const productTitle = button.dataset.productTitle || '';
        const action = state === 'cart' ? 'Remove' : state === 'pending' ? 'Deselect' : 'Add';

        button.dataset.state = state;
        button.dataset.lineKey = lineKey;
        button.classList.toggle('is-added', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
        button.setAttribute('aria-label', `${action} ${productTitle}`.trim());
      }

      handleIdleToggle(button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        this.waitForCartMutations()
          .then(() => this.refreshCartState({ force: true }))
          .then((cart) => {
            button.disabled = false;
            button.removeAttribute('aria-busy');
            if (!button.isConnected || (button.dataset.state || 'idle') !== 'idle') return;
            if (this.isFullCart(cart) && cart.item_count > 0) this.addDirectItem(button);
            else this.setButtonState(button, 'pending');
          })
          .catch((error) => {
            console.error('[product-upsell] cart state check failed', error);
            button.disabled = false;
            button.removeAttribute('aria-busy');
            if (button.isConnected && (button.dataset.state || 'idle') === 'idle') {
              this.setButtonState(button, 'pending');
            }
          });
      }

      onCartLinesUpdate(event) {
        if (!event?.promise || typeof event.promise.then !== 'function') return;

        let trackedPromise;
        trackedPromise = Promise.resolve(event.promise)
          .catch(() => null)
          .finally(() => this.cartMutationPromises.delete(trackedPromise));
        this.cartMutationPromises.add(trackedPromise);
      }

      waitForCartMutations(deadline = Date.now() + 5000) {
        const pendingPromises = Array.from(this.cartMutationPromises);
        if (pendingPromises.length) {
          return Promise.allSettled(pendingPromises).then(() => this.waitForCartMutations(deadline));
        }

        const cartIsLoading = document.querySelector(
          '#main-cart-items.cart__items--disabled, #CartDrawer-CartItems.cart__items--disabled'
        );
        if (!cartIsLoading || Date.now() >= deadline) return Promise.resolve();

        return new Promise((resolve) => window.setTimeout(resolve, 50))
          .then(() => this.waitForCartMutations(deadline));
      }

      addDirectItem(button) {
        const variantId = Number(button.dataset.variantId);
        if (!variantId) return;

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        this.addDeferredItems([variantId], false)
          .catch((error) => console.error('[product-upsell] direct add failed', error))
          .finally(() => {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          });
      }

      onProductBuyClick(event) {
        const button = event.target.closest('[data-atc-button], .product-form__submit');
        if (!button || !this.productInfo?.contains(button) || button.disabled) return;
        this.armDeferredAdd();
      }

      onProductFormSubmit(event) {
        if (!event.target.matches?.('form[data-type="add-to-cart-form"]')) return;
        this.armDeferredAdd();
      }

      armDeferredAdd() {
        const variantIds = Array.from(
          this.querySelectorAll('.product-upsell-toggle[data-state="pending"]')
        )
          .map((button) => Number(button.dataset.variantId))
          .filter(Boolean);
        if (!variantIds.length) return;

        this.deferredVariantIds = new Set(variantIds);
        if (this.deferredAddTimer) window.clearTimeout(this.deferredAddTimer);
        // Bundle apps can bypass the native product-form submit handler. Give
        // their primary cart request time to finish, then verify and add only
        // the selected variants that are still missing.
        this.deferredAddTimer = window.setTimeout(() => this.ensureDeferredItems(), 1200);
      }

      cancelDeferredAdd() {
        if (this.deferredAddTimer) window.clearTimeout(this.deferredAddTimer);
        this.deferredAddTimer = null;
        this.deferredVariantIds = null;
      }

      onCartError(event) {
        if (event?.source === 'product-form') this.cancelDeferredAdd();
      }

      removeCartItem(button) {
        const drawer = document.querySelector('cart-drawer');
        const root = window.Shopify?.routes?.root || '/';
        const changeUrl = typeof routes !== 'undefined' && routes.cart_change_url
          ? routes.cart_change_url
          : `${root}cart/change.js`;
        const sections = drawer?.getSectionsToRender
          ? drawer.getSectionsToRender().map((section) => section.id)
          : ['cart-drawer', 'cart-icon-bubble'];
        const lineId = button.dataset.lineKey || button.dataset.variantId;

        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: lineId,
            quantity: 0,
            sections,
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (!response || response.status || response.errors) {
              throw new Error(response?.description || response?.message || response?.errors || 'Unable to update cart');
            }

            this.applyCartState(response);
            document.dispatchEvent(new CustomEvent('vioran:cart-updated', {
              detail: { cart: response, source: 'product-upsell' },
            }));

            if (drawer) drawer.classList.toggle('is-empty', response.item_count === 0);
            if (drawer && response.sections && drawer.renderContents) drawer.renderContents(response);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              return publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-upsell',
                cartData: response,
                variantId: button.dataset.variantId,
              });
            }
          })
          .catch((error) => console.error('[product-upsell]', error))
          .finally(() => {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          });
      }

      onCartUpdate(event) {
        let cartPromise;
        if (this.isFullCart(event?.cartData)) {
          this.applyCartState(event.cartData);
          cartPromise = Promise.resolve(event.cartData);
        } else {
          this.syncAddedItems(event?.cartData);
          cartPromise = this.refreshCartState({ force: true });
        }

        if (this.deferredVariantIds?.size && event?.source !== 'product-upsell-deferred') {
          return cartPromise.then((cart) => this.ensureDeferredItems(cart));
        }
        return cartPromise;
      }

      onExternalCartUpdate(event) {
        let cartPromise;
        if (this.isFullCart(event.detail?.cart)) {
          this.applyCartState(event.detail.cart);
          cartPromise = Promise.resolve(event.detail.cart);
        } else {
          this.syncAddedItems(event.detail?.cart);
          cartPromise = this.refreshCartState({ force: true });
        }

        if (this.deferredVariantIds?.size && event.detail?.source !== 'product-upsell-deferred') {
          return cartPromise.then((cart) => this.ensureDeferredItems(cart));
        }
        return cartPromise;
      }

      onPageShow() {
        this.refreshCartState();
      }

      isFullCart(cart) {
        return Boolean(cart && Array.isArray(cart.items) && typeof cart.item_count === 'number');
      }

      ensureDeferredItems(cartData) {
        if (!this.deferredVariantIds?.size) return Promise.resolve(cartData);
        if (this.deferredAddInFlight) return this.deferredAddInFlight;
        if (this.deferredAddTimer) window.clearTimeout(this.deferredAddTimer);
        this.deferredAddTimer = null;

        const queuedVariantIds = Array.from(this.deferredVariantIds);
        const cartPromise = this.isFullCart(cartData) ? Promise.resolve(cartData) : this.refreshCartState();

        this.deferredAddInFlight = cartPromise
          .then((cart) => {
            const presentVariantIds = new Set(
              (cart?.items || []).map((item) => Number(item.variant_id || item.id))
            );
            const missingVariantIds = queuedVariantIds.filter((variantId) => !presentVariantIds.has(variantId));

            if (!missingVariantIds.length) {
              this.deferredVariantIds = null;
              if (this.isFullCart(cart)) this.applyCartState(cart);
              return cart;
            }

            return this.addDeferredItems(missingVariantIds);
          })
          .catch((error) => {
            console.error('[product-upsell] deferred add failed', error);
            return null;
          })
          .finally(() => {
            this.deferredAddInFlight = null;
          });

        return this.deferredAddInFlight;
      }

      addDeferredItems(variantIds, clearDeferredQueue = true) {
        const drawer = document.querySelector('cart-drawer');
        const root = window.Shopify?.routes?.root || '/';
        const addUrl = typeof routes !== 'undefined' && routes.cart_add_url
          ? routes.cart_add_url
          : `${root}cart/add.js`;
        const sections = drawer?.getSectionsToRender
          ? drawer.getSectionsToRender().map((section) => section.id)
          : ['cart-drawer', 'cart-icon-bubble'];

        return fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: variantIds.map((id) => ({ id, quantity: 1 })),
            sections,
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((response) => {
            if (!response || response.status || response.errors) {
              throw new Error(response?.description || response?.message || response?.errors || 'Unable to add upsell');
            }

            const addedItems = Array.isArray(response.items) ? response.items : [response];
            variantIds.forEach((variantId) => {
              const lineItem = addedItems.find((item) => Number(item.variant_id || item.id) === variantId);
              this.querySelectorAll(`.product-upsell-toggle[data-variant-id="${variantId}"]`).forEach((button) => {
                this.setButtonState(button, 'cart', lineItem?.key || '');
              });
            });
            if (clearDeferredQueue) this.deferredVariantIds = null;

            document.dispatchEvent(new CustomEvent('vioran:cart-updated', {
              detail: { cart: response, source: 'product-upsell-deferred' },
            }));
            if (drawer) drawer.classList.remove('is-empty');
            if (drawer && response.sections && drawer.renderContents) drawer.renderContents(response);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-upsell-deferred',
                cartData: response,
              }).catch((error) => console.error('[product-upsell]', error));
            }
            return response;
          });
      }

      refreshCartState({ force = false } = {}) {
        if (!force && this.cartRequest) return this.cartRequest;
        if (force && this.freshCartRequest) return this.freshCartRequest;

        const cartUrl = typeof routes !== 'undefined' ? routes.cart_url : '/cart';
        const request = force
          ? fetch(`${cartUrl}.json?product_upsell_state=${Date.now()}`, {
              headers: { Accept: 'application/json' },
              cache: 'no-store',
            }).then((response) => response.json())
          : typeof CartItems !== 'undefined' && CartItems.fetchCartData
            ? CartItems.fetchCartData()
            : fetch(`${cartUrl}.json`).then((response) => response.json());
        const requestSequence = ++this.cartStateSequence;

        let requestPromise;
        requestPromise = Promise.resolve(request)
          .then((cart) => {
            if (this.isFullCart(cart) && requestSequence >= this.appliedCartStateSequence) {
              this.appliedCartStateSequence = requestSequence;
              this.syncFromCart(cart);
            }
            return cart;
          })
          .catch((error) => {
            console.error('[product-upsell]', error);
            return null;
          })
          .finally(() => {
            if (this.cartRequest === requestPromise) this.cartRequest = null;
            if (this.freshCartRequest === requestPromise) this.freshCartRequest = null;
          });

        if (force) this.freshCartRequest = requestPromise;
        else this.cartRequest = requestPromise;
        return requestPromise;
      }

      applyCartState(cart) {
        if (!this.isFullCart(cart)) return;
        this.appliedCartStateSequence = ++this.cartStateSequence;
        this.syncFromCart(cart);
      }

      syncAddedItems(cartData) {
        if (!cartData) return;

        const addedItems = Array.isArray(cartData.items)
          ? cartData.items
          : cartData.variant_id || cartData.id
            ? [cartData]
            : [];

        addedItems.forEach((item) => {
          const variantId = String(item.variant_id || item.id || '');
          if (!variantId) return;

          this.querySelectorAll(`.product-upsell-toggle[data-variant-id="${variantId}"]`).forEach((button) => {
            this.setButtonState(button, 'cart', item.key || '');
          });
        });
      }

      syncFromCart(cart) {
        if (!Array.isArray(cart?.items)) return;
        this.latestCart = cart;
        const cartIsEmpty = cart.item_count === 0;
        const cartBecameEmpty = this.lastCartItemCount !== null && this.lastCartItemCount > 0 && cartIsEmpty;

        const lineKeysByVariant = new Map();
        cart.items.forEach((item) => {
          const variantId = String(item.variant_id || item.id || '');
          if (variantId && !lineKeysByVariant.has(variantId)) {
            lineKeysByVariant.set(variantId, item.key || '');
          }
        });

        this.querySelectorAll('.product-upsell-toggle').forEach((button) => {
          const variantId = String(button.dataset.variantId || '');
          if (lineKeysByVariant.has(variantId)) {
            this.setButtonState(button, 'cart', lineKeysByVariant.get(variantId));
          } else if (!button.disabled && cartIsEmpty && (cartBecameEmpty || button.dataset.state === 'cart')) {
            this.setButtonState(button, 'pending');
          } else if (button.dataset.state === 'cart') {
            this.setButtonState(button, 'idle');
          }
        });

        this.lastCartItemCount = cart.item_count;
      }
    }
  );
}
